import { faPencil, faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ColDef, ICellRendererParams } from 'ag-grid-community';

import { Blueprint } from '@/types/blueprint';
import { Pill } from '@/ui';

export interface BlueprintActionHandlers {
  canDelete?: boolean;
  canEdit?: boolean;
  onDelete: (blueprint: Blueprint) => void;
  onEdit: (blueprint: Blueprint) => void;
}

const STATUS_LABELS: Record<string, string> = {
  open: 'Open',
  in_progress: 'In Progress',
  snoozed: 'Snoozed',
  closed: 'Closed / Archived'
};

const STATUS_COLORS: Record<string, 'primary' | 'warning' | 'neutral'> = {
  open: 'primary',
  in_progress: 'warning',
  snoozed: 'neutral',
  closed: 'neutral'
};

const assigneeName = (blueprint: Blueprint) => {
  if (!blueprint.assignee) return '';
  return `${blueprint.assignee.first_name ?? ''} ${blueprint.assignee.last_name ?? ''}`.trim();
};

const statusCellRenderer = (params: ICellRendererParams<Blueprint>) => {
  const status = params.data?.status;
  if (!status) return '';
  return (
    <Pill
      size="sm"
      variant="subtle"
      color={STATUS_COLORS[status] ?? 'neutral'}
      className="inline leading-none">
      {STATUS_LABELS[status] ?? status}
    </Pill>
  );
};

const assigneeCellRenderer = (params: ICellRendererParams<Blueprint>) =>
  params.data ? assigneeName(params.data) : '';

const tagsCellRenderer = (params: ICellRendererParams<Blueprint>) => {
  const tags = params.data?.tags ?? [];
  if (tags.length === 0) return '';
  return (
    <div className="flex flex-wrap items-center gap-1 py-1">
      {tags.map((tag) => (
        <Pill
          key={tag.id}
          size="sm"
          variant="outline"
          color="neutral"
          className="inline leading-none">
          {tag.name}
        </Pill>
      ))}
    </div>
  );
};

const actionsCellRenderer = ({
  canDelete = true,
  canEdit = true,
  onDelete,
  onEdit
}: BlueprintActionHandlers) =>
  function ActionsCell(params: ICellRendererParams<Blueprint>) {
    const blueprint = params.data;
    if (!blueprint) return '';
    return (
      <div className="flex h-full items-center gap-3">
        {canEdit && (
          <button
            type="button"
            aria-label="Edit"
            onClick={() => onEdit(blueprint)}
            className="text-uniform-gray-500 hover:text-uniform-gray-700">
            <FontAwesomeIcon icon={faPencil} />
          </button>
        )}
        {canDelete && (
          <button
            type="button"
            aria-label="Delete"
            onClick={() => onDelete(blueprint)}
            className="text-uniform-gray-500 hover:text-uniform-red-600">
            <FontAwesomeIcon icon={faTrash} />
          </button>
        )}
      </div>
    );
  };

export const createColumnDefs = (handlers: BlueprintActionHandlers): ColDef<Blueprint>[] => [
  { field: 'name', headerName: 'Name', sortable: true, filter: false, flex: 2 },
  { field: 'key', headerName: 'Key', sortable: true, filter: false, flex: 2 },
  { field: 'title', headerName: 'Title', sortable: true, filter: false, flex: 2 },
  {
    field: 'taskable_type_label',
    headerName: 'Taskable type',
    sortable: true,
    filter: false,
    flex: 1
  },
  {
    field: 'status',
    headerName: 'Default status',
    sortable: true,
    filter: false,
    flex: 1,
    cellRenderer: statusCellRenderer
  },
  {
    colId: 'assignee',
    headerName: 'Assignee',
    sortable: false,
    filter: false,
    flex: 1,
    cellRenderer: assigneeCellRenderer
  },
  {
    colId: 'tags',
    headerName: 'Tags',
    sortable: false,
    filter: false,
    flex: 2,
    autoHeight: true,
    cellRenderer: tagsCellRenderer
  },
  {
    colId: 'actions',
    headerName: '',
    sortable: false,
    filter: false,
    resizable: false,
    flex: 0,
    width: 100,
    cellRenderer: actionsCellRenderer(handlers)
  }
];

export const COLUMN_ORDERING_MAP: Record<string, string> = {
  name: 'name',
  key: 'key',
  title: 'title',
  status: 'status',
  taskable_type_label: 'taskable_type__model'
};
